import {
  getBeaconOptions,
  getCamera3dOptions,
  getCamera3dQueryKey,
  getCameraOptions,
  getCameraQueryKey,
  getGpsOptions,
  getServerOptions,
  postGpsRestartMutation,
  postRoofForceRestartMutation,
  postRoofForceShutdownMutation,
  postRoofRestartMutation,
  postRoofShutdownMutation,
  postServerForceRestartMutation,
  postServerForceShutdownMutation,
  postServerRestartMutation,
  postServerShutdownMutation,
  putBeaconMutation,
  putCamera3dMutation,
  putCameraMutation,
  putGpsMutation,
  restartCamera3dMutation,
  restartCameraMutation,
} from '@/api/controlUnitApi'
import { QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Client } from '@/api/controlUnitApi/client/types.gen'
import { notifications } from '@mantine/notifications'
import { useState, useCallback, useRef } from 'react'
import { Camera3dPosition, CameraPosition } from '@/api/controlUnitApi/types.gen'
import { useTranslation } from 'react-i18next'
import { extendKey } from '@/utils/queryKey'

// Device restart timeouts (in milliseconds)
const RESTART_TIMEOUTS = {
  gps: 120000, // 2 minutes
  cameraFL: 150000, // 2.5 minutes
  cameraFC: 150000, // 2.5 minutes
  cameraFR: 150000, // 2.5 minutes
  camera3dFL: 180000, // 3 minutes
  camera3dFR: 180000, // 3 minutes
  server: 300000, // 5 minutes
  kit: 300000, // 5 minutes
} as const

export type RestartsDeviceType = keyof typeof RESTART_TIMEOUTS | 'beacon'

const getCameraKey = (pos: CameraPosition) => (pos === 'FL' ? 'frontLeft' : pos === 'FC' ? 'frontCenter' : 'frontRight')

const getCamera3dKey = (pos: Camera3dPosition) => (pos === 'FR' ? 'frontRight' : 'frontLeft')

const determineCameraQueryKey =
  (client: Client, source: string) =>
  (cameraPosition: CameraPosition): QueryKey => [
    ...getCameraQueryKey({ client, path: { camera_position: cameraPosition } }),
    source,
  ]
const determineCamera3dQueryKey =
  (client: Client, source: string) =>
  (cameraPosition: Camera3dPosition): QueryKey => [
    ...getCamera3dQueryKey({ client, path: { camera_position: cameraPosition } }),
    source,
  ]

const determineDeviceType = (cameraPosition: CameraPosition) => {
  return cameraPosition === 'FL' ? 'cameraFL' : cameraPosition === 'FC' ? 'cameraFC' : 'cameraFR'
}
const determineCamera3dDeviceType = (cameraPosition: Camera3dPosition) => {
  return cameraPosition === 'FL' ? 'camera3dFL' : cameraPosition === 'FR' ? 'camera3dFR' : 'camera3dFL'
}

const useRestartsPanelData = ({ client, source }: { client: Client; source: string }) => {
  const { t } = useTranslation('hardware')
  const queryClient = useQueryClient()

  // Device mutex state - keeps track of restart operations
  const [deviceRestarting, setDeviceRestarting] = useState<Set<RestartsDeviceType>>(new Set())
  const [cameraToggleStates, setCameraToggleStates] = useState<Map<string, boolean>>(new Map())
  const [camera3dToggleStates, setCamera3dToggleStates] = useState<Map<string, boolean>>(new Map())

  const timeoutRefs = useRef<Record<RestartsDeviceType, NodeJS.Timeout>>(
    {} as Record<RestartsDeviceType, NodeJS.Timeout>
  )

  // Helper to check if device can be operated on
  const isDeviceAvailable = useCallback(
    (deviceId: RestartsDeviceType) => {
      return !deviceRestarting.has(deviceId)
    },
    [deviceRestarting]
  )

  // Helper to mark device as restarting and set timeout
  const markDeviceRestartingWithTimeout = useCallback(
    (deviceId: RestartsDeviceType) => {
      setDeviceRestarting(prev => new Set(prev).add(deviceId))

      const timeout = RESTART_TIMEOUTS[deviceId as keyof typeof RESTART_TIMEOUTS]
      if (timeout) {
        timeoutRefs.current[deviceId] = setTimeout(() => {
          setDeviceRestarting(prev => {
            const next = new Set(prev)
            next.delete(deviceId)
            return next
          })

          notifications.show({
            title: t('deviceTimeout'),
            message: t('deviceRestartTimeout', { device: deviceId }),
            color: 'yellow',
            autoClose: 10000,
          })
        }, timeout)
      }
    },
    [t]
  )

  // Helper to mark device as available again
  const markDeviceAvailable = useCallback((deviceId: RestartsDeviceType) => {
    setDeviceRestarting(prev => {
      const next = new Set(prev)
      next.delete(deviceId)
      return next
    })

    // Clear any pending timeout
    if (timeoutRefs.current[deviceId]) {
      clearTimeout(timeoutRefs.current[deviceId])
      delete timeoutRefs.current[deviceId]
    }
  }, [])

  const getCameraQueryKey = determineCameraQueryKey(client, source)
  const getCamera3dQueryKey = determineCamera3dQueryKey(client, source)

  const gpsOpts = extendKey(getGpsOptions({ client }), source)
  const beaconOpts = extendKey(getBeaconOptions({ client }), source)
  const serverOpts = extendKey(getServerOptions({ client }), source)
  const gpsQueryKey: QueryKey = gpsOpts.queryKey
  const beaconQueryKey: QueryKey = beaconOpts.queryKey
  const serverQueryKey: QueryKey = serverOpts.queryKey

  const { data: gpsState } = useQuery({ ...gpsOpts, retry: 1 })

  const cameraFlOpts = extendKey(getCameraOptions({ client, path: { camera_position: 'FL' } }), source)
  const cameraFcOpts = extendKey(getCameraOptions({ client, path: { camera_position: 'FC' } }), source)
  const cameraFrOpts = extendKey(getCameraOptions({ client, path: { camera_position: 'FR' } }), source)
  const { data: cameraFlState } = useQuery({ ...cameraFlOpts, retry: 1 })
  const { data: cameraFcState } = useQuery({ ...cameraFcOpts, retry: 1 })
  const { data: cameraFrState } = useQuery({ ...cameraFrOpts, retry: 1 })

  const camera3dFlOpts = extendKey(getCamera3dOptions({ client, path: { camera_position: 'FL' } }), source)
  const camera3dFrOpts = extendKey(getCamera3dOptions({ client, path: { camera_position: 'FR' } }), source)
  const { data: camera3dFlState } = useQuery({ ...camera3dFlOpts, retry: 1 })
  const { data: camera3dFrState } = useQuery({ ...camera3dFrOpts, retry: 1 })

  const { data: beaconState } = useQuery({ ...beaconOpts, retry: 1 })

  const { data: serverState } = useQuery({ ...serverOpts, retry: 1 })

  const { mutate: restartGps, isPending: isRestartingGps } = useMutation({
    ...postGpsRestartMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('gps')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('gps') }),
          color: 'orange',
        })
        throw new Error('GPS device is busy')
      }
      markDeviceRestartingWithTimeout('gps')
      notifications.show({
        title: t('gpsRestartInitiated'),
        message: t('gpsRestartInitiatedMsg'),
        color: 'blue',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gpsQueryKey })
      markDeviceAvailable('gps')
      notifications.show({
        title: t('gpsRestartComplete'),
        message: t('gpsRestartedSuccessfully'),
        color: 'green',
      })
    },
    onError: error => {
      markDeviceAvailable('gps')
      if (error.message !== 'GPS device is busy') {
        notifications.show({
          title: t('gpsRestartFailed'),
          message: t('failedToRestartGps'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: toggleGps, isPending: isTogglingGps } = useMutation({
    ...putGpsMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('gps')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('gps') }),
          color: 'orange',
        })
        throw new Error('GPS device is busy')
      }
    },
    onSuccess: ({ enabled }) => {
      queryClient.invalidateQueries({ queryKey: gpsQueryKey })
      notifications.show({
        title: t('gpsStateChanged'),
        message: t('gpsStateMsg', { state: t(enabled ? 'enabled' : 'disabled') }),
        color: 'green',
      })
    },
    onError: error => {
      if (error.message !== 'GPS device is busy') {
        notifications.show({
          title: t('gpsToggleFailed'),
          message: t('failedToToggleGps'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: toggleCamera } = useMutation({
    ...putCameraMutation({ client }),
    onMutate: ({ path: { camera_position }, body: { enabled } }) => {
      const deviceType = determineDeviceType(camera_position)

      if (!isDeviceAvailable(deviceType)) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t(getCameraKey(camera_position)) }),
          color: 'orange',
        })
        throw new Error('Camera device is busy')
      }
      setCameraToggleStates(prev => new Map(prev).set(camera_position, enabled))
    },
    onSuccess: ({ enabled }, { path: { camera_position } }) => {
      queryClient.invalidateQueries({ queryKey: getCameraQueryKey(camera_position) })
      notifications.show({
        title: t('cameraStateChanged'),
        message: t('cameraStateMsg', {
          camera: t(getCameraKey(camera_position)),
          state: t(enabled ? 'enabled' : 'disabled'),
        }),
        color: 'green',
      })
    },
    onSettled: (_, __, { path: { camera_position } }) => {
      setCameraToggleStates(prev => {
        const next = new Map(prev)
        next.delete(camera_position)
        return next
      })
    },
    onError: (error, { path: { camera_position } }) => {
      if (error.message !== 'Camera device is busy') {
        notifications.show({
          title: t('cameraToggleFailed'),
          message: t('failedToToggleCamera', { camera: t(getCameraKey(camera_position)) }),
          color: 'red',
        })
      }
    },
  })

  const { mutate: restartCamera, isPending: isRestartingCamera } = useMutation({
    ...restartCameraMutation({ client }),
    onMutate: ({ path: { camera_position } }) => {
      const deviceType = determineDeviceType(camera_position)

      if (!isDeviceAvailable(deviceType)) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t(getCameraKey(camera_position)) }),
          color: 'orange',
        })
        throw new Error('Camera device is busy')
      }
      markDeviceRestartingWithTimeout(deviceType)
      notifications.show({
        title: t('cameraRestartInitiated'),
        message: t('cameraRestartInitiatedMsg', { camera: t(getCameraKey(camera_position)) }),
        color: 'blue',
      })
    },
    onSuccess: (_, { path: { camera_position } }) => {
      const deviceType = determineDeviceType(camera_position)
      queryClient.invalidateQueries({ queryKey: getCameraQueryKey(camera_position) })
      markDeviceAvailable(deviceType)
      notifications.show({
        title: t('cameraRestartComplete'),
        message: t('cameraRestartedSuccessfully', { camera: t(getCameraKey(camera_position)) }),
        color: 'green',
      })
    },
    onError: (error, { path: { camera_position } }) => {
      const deviceType = determineDeviceType(camera_position)

      markDeviceAvailable(deviceType)
      if (error.message !== 'Camera device is busy') {
        notifications.show({
          title: t('cameraRestartFailed'),
          message: t('failedToRestartCamera'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: restartCamera3d, isPending: isRestartingCamera3d } = useMutation({
    ...restartCamera3dMutation({ client }),
    onMutate: ({ path: { camera_position } }) => {
      const deviceType = determineCamera3dDeviceType(camera_position)

      if (!isDeviceAvailable(deviceType)) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t(getCamera3dKey(camera_position)) }),
          color: 'orange',
        })
        throw new Error('Camera3D device is busy')
      }
      markDeviceRestartingWithTimeout(deviceType)
      notifications.show({
        title: t('camera3dRestartInitiated'),
        message: t('camera3dRestartInitiatedMsg', { camera: t(getCamera3dKey(camera_position)) }),
        color: 'blue',
      })
    },
    onSuccess: (_, { path: { camera_position } }) => {
      const deviceType = determineCamera3dDeviceType(camera_position)

      queryClient.invalidateQueries({ queryKey: getCamera3dQueryKey(camera_position) })
      markDeviceAvailable(deviceType)
      notifications.show({
        title: t('camera3dRestartComplete'),
        message: t('camera3dRestartedSuccessfully', { camera: t(getCamera3dKey(camera_position)) }),
        color: 'green',
      })
    },
    onError: (error, { path: { camera_position } }) => {
      const deviceType = determineCamera3dDeviceType(camera_position)

      markDeviceAvailable(deviceType)
      if (error.message !== 'Camera3D device is busy') {
        notifications.show({
          title: t('camera3dRestartFailed'),
          message: t('failedToRestartCamera3d'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: toggleCamera3d, isPending: isTogglingCamera3d } = useMutation({
    ...putCamera3dMutation({ client }),
    onMutate: ({ path: { camera_position }, body: { enabled } }) => {
      const deviceType = determineCamera3dDeviceType(camera_position)

      if (!isDeviceAvailable(deviceType)) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t(getCamera3dKey(camera_position)) }),
          color: 'orange',
        })
        throw new Error('Camera3D device is busy')
      }
      setCamera3dToggleStates(prev => new Map(prev).set(camera_position, enabled))
    },
    onSuccess: ({ enabled }, { path: { camera_position } }) => {
      queryClient.invalidateQueries({ queryKey: getCamera3dQueryKey(camera_position) })
      notifications.show({
        title: t('camera3dStateChanged'),
        message: t('camera3dStateMsg', {
          camera: t(getCamera3dKey(camera_position)),
          state: t(enabled ? 'enabled' : 'disabled'),
        }),
        color: 'green',
      })
    },
    onSettled: (_, __, { path: { camera_position } }) => {
      setCamera3dToggleStates(prev => {
        const next = new Map(prev)
        next.delete(camera_position)
        return next
      })
    },
    onError: (error, { path: { camera_position } }) => {
      if (error.message !== 'Camera3D device is busy') {
        notifications.show({
          title: t('camera3dToggleFailed'),
          message: t('failedToToggleCamera3d', { camera: t(getCamera3dKey(camera_position)) }),
          color: 'red',
        })
      }
    },
  })

  const { mutate: toggleBeacon, isPending: isTogglingBeacon } = useMutation({
    ...putBeaconMutation({ client }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: beaconQueryKey })
      notifications.show({
        title: t('beaconStateChanged'),
        message: t('beaconStateChangedMsg'),
      })
    },
  })

  const { mutate: shutdownServer, isPending: isShuttingDownServer } = useMutation({
    ...postServerShutdownMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('server')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyShuttingDown', { device: t('server') }),
          color: 'orange',
        })
        throw new Error('Server is busy')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryKey })
      notifications.show({
        title: t('serverShutdown'),
        message: t('serverShutdownMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Server is busy') {
        notifications.show({
          title: t('serverShutdownFailed'),
          message: t('failedToShutdownServer'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: forceShotDownServer, isPending: isForceShuttingDownServer } = useMutation({
    ...postServerForceShutdownMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('server')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyShuttingDown', { device: t('server') }),
          color: 'orange',
        })
        throw new Error('Server is busy')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryKey })
      notifications.show({
        title: t('serverForceShutdown'),
        message: t('serverForceShutdownMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Server is busy') {
        notifications.show({
          title: t('serverForceShutdownFailed'),
          message: t('failedToForceShutdownServer'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: restartServer, isPending: isRestartingServer } = useMutation({
    ...postServerRestartMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('server')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('server') }),
          color: 'orange',
        })
        throw new Error('Server is busy')
      }
      markDeviceRestartingWithTimeout('server')
      notifications.show({
        title: t('serverRestartInitiated'),
        message: t('serverRestartInitiatedMsg'),
        color: 'blue',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryKey })
      markDeviceAvailable('server')
      notifications.show({
        title: t('serverRestartComplete'),
        message: t('serverRestartedSuccessfully'),
        color: 'green',
      })
    },
    onError: error => {
      markDeviceAvailable('server')
      if (error.message !== 'Server is busy') {
        notifications.show({
          title: t('serverRestartFailed'),
          message: t('failedToRestartServer'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: forceRestartServer, isPending: isForceRestartingServer } = useMutation({
    ...postServerForceRestartMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('server')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('server') }),
          color: 'orange',
        })
        throw new Error('Server is busy')
      }
      markDeviceRestartingWithTimeout('server')
      notifications.show({
        title: t('serverForceRestartInitiated'),
        message: t('serverForceRestartInitiatedMsg'),
        color: 'orange',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryKey })
      markDeviceAvailable('server')
      notifications.show({
        title: t('serverForceRestartComplete'),
        message: t('serverForceRestartCompleteMsg'),
        color: 'red',
      })
    },
    onError: error => {
      markDeviceAvailable('server')
      if (error.message !== 'Server is busy') {
        notifications.show({
          title: t('serverForceRestartFailed'),
          message: t('failedToForceRestartServer'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: shutdownKit, isPending: isShuttingDownKit } = useMutation({
    ...postRoofShutdownMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('kit')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyShuttingDown', { device: t('kit') }),
          color: 'orange',
        })
        throw new Error('Kit is busy')
      }
    },
    onSuccess: () => {
      notifications.show({
        title: t('kitShutdown'),
        message: t('kitShutdownMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Kit is busy') {
        notifications.show({
          title: t('kitShutdownFailed'),
          message: t('failedToShutdownKit'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: forceShotDownKit, isPending: isForceShuttingDownKit } = useMutation({
    ...postRoofForceShutdownMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('kit')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyShuttingDown', { device: t('kit') }),
          color: 'orange',
        })
        throw new Error('Kit is busy')
      }
    },
    onSuccess: () => {
      notifications.show({
        title: t('kitForceShutdown'),
        message: t('kitForceShutdownMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Kit is busy') {
        notifications.show({
          title: t('kitForceShutdownFailed'),
          message: t('failedToForceShutdownKit'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: restartKit, isPending: isRestartingKit } = useMutation({
    ...postRoofRestartMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('kit')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('kit') }),
          color: 'orange',
        })
        throw new Error('Kit is busy')
      }
    },
    onSuccess: () => {
      notifications.show({
        title: t('kitRestart'),
        message: t('kitRestartMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Kit is busy') {
        notifications.show({
          title: t('kitRestartFailed'),
          message: t('failedToRestartKit'),
          color: 'red',
        })
      }
    },
  })

  const { mutate: forceRestartKit, isPending: isForceRestartingKit } = useMutation({
    ...postRoofForceRestartMutation({ client }),
    onMutate: () => {
      if (!isDeviceAvailable('kit')) {
        notifications.show({
          title: t('deviceBusy'),
          message: t('deviceCurrentlyRestarting', { device: t('kit') }),
          color: 'orange',
        })
        throw new Error('Kit is busy')
      }
    },
    onSuccess: () => {
      notifications.show({
        title: t('kitForceRestart'),
        message: t('kitForceRestartMsg'),
      })
    },
    onError: error => {
      if (error.message !== 'Kit is busy') {
        notifications.show({
          title: t('kitForceRestartFailed'),
          message: t('failedToForceRestartKit'),
          color: 'red',
        })
      }
    },
  })

  return {
    gpsState,
    beaconState,
    serverState,
    cameraFlState,
    cameraFcState,
    cameraFrState,
    camera3dFlState,
    camera3dFrState,

    isRestartingGps,
    isTogglingGps,
    cameraToggleStates,
    camera3dToggleStates,
    isRestartingCamera,
    isRestartingCamera3d,
    isTogglingCamera3d,
    isTogglingBeacon,
    isShuttingDownServer,
    isForceShuttingDownServer,
    isRestartingServer,
    isForceRestartingServer,
    isShuttingDownKit,
    isForceShuttingDownKit,
    isRestartingKit,
    isForceRestartingKit,

    deviceRestarting,
    isDeviceAvailable,

    shutdownServer,
    forceShotDownServer,
    restartServer,
    forceRestartServer,
    toggleBeacon,
    restartCamera3d,
    restartCamera,
    restartGps,
    toggleGps,
    toggleCamera3d,
    shutdownKit,
    forceShotDownKit,
    restartKit,
    forceRestartKit,
    toggleCamera,

    gpsQueryKey,
    beaconQueryKey,
    serverQueryKey,
  }
}

export default useRestartsPanelData
